/* The one WebSocket: the signed-in user's events, and their org's (backend/modules/event_socket).
   FRONTEND_RULE_BOOK.md §3: a socket goes through here, SSE through `stream.ts`,
   every other request through `client.ts`.

   What it does so no caller has to:

   - sends the bearer token as the `bearer, <token>` subprotocol pair — a browser
     WebSocket cannot set a header, and a URL lands in access logs;
   - remembers the last event id and reconnects from it (`?since=`), backing off
     1 s → 30 s with jitter, so a blip loses no event and repeats none;
   - pings every 20 s and gives up on a socket with no pong in 10 s: a browser
     cannot see protocol pings, so a half-open socket would look alive forever;
   - reconnects at once when the network comes back;
   - stops for good on 4403 (not a user). 4401 (login expired) is an ordinary
     reconnect: by then the auth SDK holds a fresh token;
   - follows the runs' traces something asked for (`watchTrace`), and asks for
     them again on every reconnect, each from the last record it got. */
import { client, currentToken } from "./client";

import type { ServerEvent } from "./events.gen";
import type { TraceRecord } from "@/shared/types";

export const PING_MS = 20_000;
export const PONG_TIMEOUT_MS = 10_000;
export const FIRST_RETRY_MS = 1_000;
export const LAST_RETRY_MS = 30_000;
const NOT_A_USER = 4403;

/** One record of a watched run's trace. `cursor`, not `id`: the socket's own
    resume point is untouched by it. */
export interface TraceFrame {
  type: "trace";
  run_id: string;
  cursor: string;
  record: TraceRecord;
}

/** A refusal. `run_id` is set when it refuses a trace watch. */
export interface SocketError {
  type: "error";
  data: { code: string; message: string; run_id?: unknown };
}

/** What reaches `onMessage`: an event, or one of the socket's own. Trace frames
    go to their watchers instead. */
export type SocketMessage = ServerEvent | SocketError | { type: "resync"; id: string } | { type: "pong" };

interface Watch {
  cursor: string;
  /** `cursor` is where the record was: a read or a watch after it picks up next. */
  onRecord: (record: TraceRecord, cursor: string) => void;
  onRefused: (error: SocketError["data"]) => void;
}

/* One socket, so one set of watches. `send` is the open socket's, while one is. */
const watches = new Map<string, Watch>();
let send: ((message: object) => void) | null = null;

/** Follow one run's trace from `since` (the cursor `GET /runs/{id}/trace` gave).
    Each record goes to `onRecord`, the last one being `{end}`; a refusal goes to
    `onRefused`. Survives reconnects. One watcher per run: a second replaces the
    first. Returns the function that stops it. */
export function watchTrace(
  runId: string,
  since: string,
  onRecord: Watch["onRecord"],
  onRefused: Watch["onRefused"] = (error) => console.warn("socket: trace watch refused", error),
): () => void {
  const watch: Watch = { cursor: since, onRecord, onRefused };
  watches.set(runId, watch);
  send?.({ type: "trace.watch", run_id: runId, since });
  return () => {
    if (watches.get(runId) !== watch) return; // ended, or replaced
    watches.delete(runId);
    send?.({ type: "trace.unwatch", run_id: runId });
  };
}

function followTrace(frame: TraceFrame): void {
  const watch = watches.get(frame.run_id);
  if (!watch) return; // unwatched while it was on its way
  watch.cursor = frame.cursor;
  if ("end" in frame.record) watches.delete(frame.run_id); // the server stopped sending
  watch.onRecord(frame.record, frame.cursor);
}

/** A refused watch, to its watcher. True when the message was one. */
function refusedWatch(message: SocketMessage): boolean {
  if (message.type !== "error" || typeof message.data.run_id !== "string") return false;
  const watch = watches.get(message.data.run_id);
  watches.delete(message.data.run_id);
  watch?.onRefused(message.data);
  return true;
}

/** Open the socket and keep it open. Returns the function that closes it for good. */
export function connectSocket(onMessage: (message: SocketMessage) => void): () => void {
  let socket: WebSocket | null = null;
  let lastId = "";
  let failures = 0;
  let stopped = false;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let ping: ReturnType<typeof setInterval> | undefined;
  let pongDue: ReturnType<typeof setTimeout> | undefined;

  const quiet = () => {
    clearInterval(ping);
    clearTimeout(pongDue);
    pongDue = undefined;
    send = null;
  };

  /** Drop this socket without waiting for a close handshake a dead peer never answers. */
  const reconnect = (delay: number) => {
    quiet();
    if (socket) {
      socket.onclose = socket.onmessage = socket.onopen = null;
      socket.close();
      socket = null;
    }
    clearTimeout(retry);
    if (!stopped) retry = setTimeout(open, delay);
  };

  const open = () => {
    const token = currentToken();
    const ws = (socket = new WebSocket(socketUrl(lastId), token ? ["bearer", token] : []));
    ws.onopen = () => {
      send = (message) => ws.send(JSON.stringify(message));
      watches.forEach((watch, runId) => send?.({ type: "trace.watch", run_id: runId, since: watch.cursor }));
      ping = setInterval(() => {
        ws.send(JSON.stringify({ type: "ping" }));
        pongDue ??= setTimeout(() => reconnect(0), PONG_TIMEOUT_MS);
      }, PING_MS);
    };
    ws.onmessage = (frame) => {
      let message: SocketMessage | TraceFrame;
      try {
        message = JSON.parse(frame.data);
      } catch (err) {
        console.warn("socket: skipped a frame that is not JSON", err);
        return;
      }
      failures = 0; // not on open: a login refused right after the handshake must still back off
      if (message.type === "pong") {
        clearTimeout(pongDue);
        pongDue = undefined;
        return;
      }
      if (message.type === "trace") return followTrace(message);
      if (refusedWatch(message)) return;
      if ("id" in message) lastId = message.id;
      onMessage(message);
    };
    ws.onclose = (event) => {
      if (event.code === NOT_A_USER) {
        console.warn("socket: events are for a signed-in user; not reconnecting");
        stopped = true;
      }
      reconnect(backoff(failures++));
    };
  };

  const onOnline = () => reconnect(0);
  window.addEventListener("online", onOnline);
  open();
  return () => {
    stopped = true;
    window.removeEventListener("online", onOnline);
    reconnect(0);
  };
}

/** `…/api/v1` on http(s) → `…/api/v1/events` on ws(s), with the resume point. */
export function socketUrl(since: string): string {
  const url = new URL(`${client.defaults.baseURL ?? ""}/events`, window.location.href);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  if (since) url.searchParams.set("since", since);
  return url.toString();
}

/** 1 s, 2 s, 4 s … 30 s, each plus up to half a second so a fleet doesn't reconnect in step. */
const backoff = (failures: number) =>
  Math.min(FIRST_RETRY_MS * 2 ** failures, LAST_RETRY_MS) + Math.random() * 500;
